import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ChevronRight, ChevronLeft } from 'lucide-react';

import SelectRegion from '@/app/region/SelectRegion';
import SelectLocation from '@/app/location/SelectLocation';

import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import Map from '@/components/form/Map';

import { getLocationDetailsSchema } from '../../_validator';

const LocationDetailsForm = ({
  initialValues,
  onHandleSubmit,
  activity,
  onPreviousStep
}) => {
  const form = useForm({
    resolver: zodResolver(
      getLocationDetailsSchema(activity.isLocationRequired)
    ),
    defaultValues: { ...initialValues }
  });

  const onSubmit = (values) => {
    onHandleSubmit(values);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="space-y-4">
          <FormField
            control={form.control}
            name="regionId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Region</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectRegion
                      value={field.value}
                      onChange={(value) => {
                        if (value !== field.value) {
                          field.onChange(value);
                          form.setValue('locationId', null);
                        }
                      }}
                    />
                  </FormControl>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          {activity.isLocationRequired && (
            <FormField
              control={form.control}
              name="locationId"
              key={form.watch('regionId')}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Location</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectLocation
                        key={form.watch('regionId')}
                        value={field.value}
                        onChange={field.onChange}
                        disabled={!form.watch('regionId')}
                        regionId={form.watch('regionId')}
                        activityId={activity.id}
                      />
                    </FormControl>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
          <FormItem className="flex items-center gap-2">
            <FormControl>
              <Switch
                checked={form.watch('isMeetingSpot')}
                onCheckedChange={(checked) =>
                  form.setValue('isMeetingSpot', checked)
                }
              />
            </FormControl>
            <div className="pb-2">
              <FormLabel>
                <h2>Meeting Spot</h2>
              </FormLabel>
            </div>
          </FormItem>
          {form.watch('isMeetingSpot') && (
            <>
              <Map />
              <FormField
                control={form.control}
                name="locationName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Meeting Spot Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="locationDetails"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Extra Details</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Enter location details"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </>
          )}
        </div>
        <div className="flex justify-between pt-8">
          <Button
            type="button"
            className="gap-1"
            onClick={onPreviousStep}
            variant="outline"
          >
            <ChevronLeft />
            Previous
          </Button>
          <Button type="submit" className="gap-1">
            Next
            <ChevronRight />
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default LocationDetailsForm;
